import { call, all, put, takeLatest, select } from 'redux-saga/effects';
import AsyncStorage from '@react-native-community/async-storage';

import api from '../../../services/api';
import notification from '../../../services/notification';
import history from '../../../services/history';
import loadControlService from './load-control-service';
import loadTypeService from '../load-type/load-type-service';
import orderService from '../order/order-service';
import * as loadControlActions from './load-control-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  CANCEL_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM
} from './load-control-constants';
import { ISA_ERROR, ISA_USER } from '../../../constants';
import { formatMessage } from '~/i18n/translate';

const INITIAL_VALUES = {
  name: '',
  sellers: [],
  ordersIds: []
};

export function* getAll({ status = 'open' }: any) {
  try {
    yield put(loadControlActions.setLoadingData(true));

    const { data: loadTypes } = yield call(loadTypeService.getAll);
    const { data: sellers } = yield call(api.get, '/user/vendors');
    const dateUser = yield call(AsyncStorage.getItem, ISA_USER);
    const user = dateUser ? JSON.parse(dateUser) : null;

    const { data } = yield call(loadControlService.getAll, status);
    if (data) {
      yield put(
        loadControlActions.getAllSuccess([
          ...data?.map(item => {
            const loadType = loadTypes?.find(l => l._id === item.loadType);
            const vendors = item?.sellersIds?.map(id => ({
              ...sellers.find(s => s._id === id)
            }));
            return {
              ...item,
              isExternal: item?.ownerId !== user?._id,
              loadTypeName: `${loadType?.code}-${loadType?.name}`,
              vendorNames:
                vendors?.length > 0
                  ? vendors?.map(vendor => `${vendor?.externalCode}-${vendor?.name}`)
                  : ['NENHUM VENDEDOR ASSOCIADO']
            };
          })
        ])
      );
    }

    yield put(loadControlActions.setLoadingData(false));
  } catch (error) {
    yield put(loadControlActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(loadControlActions.setLoadingData(true));

    const dateUser = yield call(AsyncStorage.getItem, ISA_USER);
    const user = dateUser ? JSON.parse(dateUser) : null;

    const { data: loadTypes } = yield call(loadTypeService.getAll);
    const { data: sellers } = yield call(api.get, '/user/vendors');
    const { data: orders } = yield call(orderService.getOrders, {
      orderStatus: ['waitingLoad']
    });

    const ordersWaitingLoad = [
      ...orders.map((order: any) => {
        const vendor = sellers?.find(s => s.externalCode === order?.userExternalCode);

        return {
          _id: order._id,
          isExternal: order?.userExternalCode !== user.externalCode,
          userExternalCode: order?.userExternalCode,
          vendorName: vendor?.name,
          clientExternalCode: order?.clientExternalCode,
          clientName: order?.names?.clientName,
          subsidiaryExternalCode: order?.orderDetails?.subsidiaryExternalCode,
          number: order?.orderNumber,
          products: order?.orderItems?.map((item: any) => ({
            externalCode: item?.productExternalCode,
            name: item?.name,
            weight: item?.netWeight,
            quantity: item?.soldQuantity,
            totalWeight: item?.netWeight * item?.soldQuantity,
            price: item?.soldPrice
          })),
          orderStatus: order?.orderStatus,
          totalValue: order?.totalPrice,
          orderTotal: order?.totalPrice,
          totalWeight: order?.orderItems?.reduce(
            (acumulator: number, item: any) => acumulator + (item?.netWeight ?? 0) * item?.soldQuantity,
            0
          )
        };
      })
    ];

    if (!id) {
      yield put(
        loadControlActions.getByIdSuccess({
          data: {
            ...INITIAL_VALUES,
            loadType: loadTypes[0]?._id,
            ordersWaitingLoad
          },
          selects: {
            loadTypes,
            sellers
          }
        })
      );
    } else {
      const { data } = yield call(loadControlService.getLoadControl, id);
      if (data) {
        yield put(
          loadControlActions.getByIdSuccess({
            data: {
              ...data,
              orders: [
                ...data?.orders?.map(order => ({
                  ...order,
                  isExternal: order?.vendorExternalCode !== user.externalCode,
                  totalValue: order?.orderTotal
                }))
              ],
              ordersWaitingLoad,
              isExternal: data?.ownerId !== user?._id,
              loadTypeID: loadTypes?.find(l => l._id === data.loadType),
              sellers: sellers.filter(s => data?.sellersIds?.indexOf(s._id) !== -1)
            },
            selects: {
              loadTypes,
              sellers
            }
          })
        );
      }
    }
    yield put(loadControlActions.setLoadingData(false));
  } catch (error) {
    yield put(loadControlActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload, validate } = data;
    yield put(loadControlActions.setLoadingSend(true));

    const { sellers, ordersWaitingLoad, ...loadControl } = payload;
    let closeLoadControl = false;

    if (validate?.isValid) {
      const { isConfirmed } = yield call(
        notification.confirm,
        formatMessage('sidebarMenuItemLoadControl'),
        formatMessage('loadControlFormClose'),
        formatMessage('loadControlFormCloseButton'),
        formatMessage('commonLabelSave')
      );
      closeLoadControl = isConfirmed;
    }

    if (id) {
      yield call(loadControlService.editLoadControl, {
        _id: id,
        ...loadControl,
        sellersIds: sellers?.map(item => item?._id),
        status: closeLoadControl ? 'closed' : 'open'
      });
    } else {
      yield call(loadControlService.createLoadControl, loadControl);
    }
    yield call(history.push, `/load-control/`, {
      date: new Date().getTime()
    });
    yield put(loadControlActions.setLoadingSend(false));
    yield call(
      notification.success,
      id
        ? closeLoadControl
          ? formatMessage('loadControlFormCloseSuccess')
          : formatMessage('loadControlFormSafeSuccess')
        : formatMessage('loadControlFormCreatedSuccess')
    );
  } catch (error) {
    yield put(loadControlActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* cancel() {
  try {
    yield put(loadControlActions.setLoadingSend(true));
    const data = yield select(state => state.loadControl?.form?.data);

    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('sidebarMenuItemLoadControl'),
      formatMessage('loadControlFormExclude')
    );

    if (isConfirmed) {
      yield call(loadControlService.cancelLoadControl, {
        ...data
      });

      yield call(history.push, `/load-control/`, {
        date: new Date().getTime()
      });
      yield put(loadControlActions.setLoadingSend(false));
      yield call(notification.success, formatMessage('loadControlFormCancelSuccess'));
    }
  } catch (error) {
    yield put(loadControlActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(loadControlActions.setLoadingSend(true));
      yield call(loadControlService.deleteLoadControl, id);
      yield put(loadControlActions.setLoadingSend(false));

      yield put(loadControlActions.getAllRequest());
      yield call(notification.success, formatMessage('loadControlFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(loadControlActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/load-control/edit/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(CANCEL_REQUEST, cancel),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem)
]);
